<template>
    <q-card
        bordered
        class="DatasetCard"
        :class="{ 'cursor-pointer': canOpenVisualization }"
        tabindex="0"
        @click="handleGoToVisualization"
        @keypress.enter="handleGoToVisualization"
    >
        <q-card-section class="DatasetCardHeader bg-primary text-white">
            <div class="flex row-stretch-center no-wrap">
                <q-icon
                    name="sym_o_graph_4"
                    size="22px"
                    color="white"
                    class="q-mr-sm"
                />
                <div class="DatasetCardTitle ellipsis">
                    {{ row.name }}
                </div>
                <q-space />
                <q-btn
                    key="info"
                    icon="info"
                    color="white"
                    dense
                    flat
                    @click.stop="handleInfoModal"
                >
                    <q-tooltip>{{ $t('general.info') }}</q-tooltip>
                </q-btn>
                <q-btn
                    icon="upload_file"
                    color="white"
                    dense
                    flat
                    @click.stop="openUpdateMenu($event)"
                >
                    <q-tooltip>{{ $t('general.dataOptions') }}</q-tooltip>
                </q-btn>
                <q-btn
                    icon="more_vert"
                    color="white"
                    dense
                    flat
                    @click.stop="openMenu(row, $event)"
                />
            </div>
        </q-card-section>

        <q-card-section class="DatasetCardBody bg-white">
            <div class="DatasetCardRow">
                <div class="text-caption">
                    {{ $t('general.updateDate') }}
                </div>
                <div class="text-body2">
                    {{ row.tst ? App.dateLocationFormat(row.tst, 'long') : '-' }}
                </div>
            </div>

            <div class="flex items-center no-wrap q-mt-sm">
                <QBadge
                    v-if="row.source"
                    :label="row.source"
                    color="primary"
                    outline
                    class="text-weight-bold q-mr-sm"
                />

                <QBadge
                    v-if="showStatusBadge"
                    :label="currentStatus.label"
                    :color="currentStatus.color"
                    outline
                    class="text-weight-bold q-mr-sm"
                />

                <QLinearProgress
                    v-if="showProgressBar"
                    size="30px"
                    indeterminate
                    :value="1"
                    :color="(statusList[row.state?.identifier] || statusList.default).color"
                    rounded
                    trackColor="white"
                    class="DatasetProgress"
                >
                    <div class="absolute-full flex flex-center">
                        <QBadge
                            :label="(statusList[row.state?.identifier] || statusList.default).label"
                            :color="(statusList[row.state?.identifier] || statusList.default).color"
                            textColor="white"
                        />
                    </div>
                </QLinearProgress>
            </div>
        </q-card-section>

        <q-menu
            v-if="updateMenuActivator"
            v-model="updateMenuOpen"
            :target="updateMenuActivator"
            @hide="cleanUpdateMenu"
        >
            <q-list dense class="text-caption">
                <q-item clickable @click="handleOpenUpdateModal">
                    <q-item-section avatar>
                        <q-icon name="upload" color="primary" />
                    </q-item-section>
                    <q-item-section>
                        {{ $t('general.updateData') }}
                    </q-item-section>
                </q-item>

                <q-item clickable @click="handleOpenReplaceModal">
                    <q-item-section avatar>
                        <q-icon name="swap_horiz" color="primary" />
                    </q-item-section>
                    <q-item-section>
                        {{ $t('general.replaceData') }}
                    </q-item-section>
                </q-item>
            </q-list>
        </q-menu>

        <q-menu
            v-if="menuActivator"
            v-model="menuOpen"
            :target="menuActivator"
            @hide="cleanSelected"
        >
            <q-list dense class="text-caption">
                <q-item
                    v-for="option in row.options.dropdown"
                    :key="option.key"
                    clickable
                    @click="handleClickOption(option)"
                >
                    <q-item-section avatar>
                        <q-icon :name="option.icon" :color="option.iconColor" />
                    </q-item-section>
                    <q-item-section>
                        {{ option.label }}
                    </q-item-section>
                </q-item>
            </q-list>
        </q-menu>
    </q-card>
</template>

<script>
import VueTypes from 'vue-types'
import { useI18n } from 'vue-i18n'
import { filtersMixin } from '@/mixins'

const STATES_KEYS = {
    RUNNING: 'RUNNING',
    SUBMITTED: 'SUBMITTED',
    FINISHED: 'FINISHED',
    ERROR: 'ERROR',
    CANCELLED: 'CANCELLED',
    TIMEOUT: 'TIMEOUT',
    INCOMPLETE: 'INCOMPLETE',
}

export default {
    name: 'DatasetCard',
    components: {
    },
    mixins: [filtersMixin],
    inject: ['App'],
    props: {
        row: VueTypes.any,
    },
    emits: ['handleEmmit'],
    setup() {
        const { tm } = useI18n()
        const STATES_DATA = {
            RUNNING: {
                label: tm('projects.datasets.states.running'), color: 'secondary', useBadge: true,
            },
            SUBMITTED: {
                label: tm('projects.datasets.states.submitted'), color: 'secondary', useBadge: true,
            },
            INCOMPLETE: {
                label: tm('projects.datasets.states.incomplete'), color: 'warning', useBadge: true,
            },
            FINISHED: {
                label: tm('projects.datasets.states.finished'), color: 'green', icon: 'task_alt', useBadge: false,
            },
            ERROR: {
                label: tm('projects.datasets.states.error'), color: 'red', icon: 'warning', useBadge: false,
            },
            WARNING: {
                label: tm('projects.datasets.states.warning'), color: 'yellow', icon: 'warning', useBadge: false,
            },
            CANCELLED: {
                label: tm('projects.datasets.states.cancelled'), color: 'red', useBadge: true,
            },
            TIMEOUT: {
                label: tm('projects.datasets.states.timeout'), color: 'yellow', useBadge: true,
            },
            default: {
                label: tm('projects.datasets.states.timeout'), color: 'secondary', useBadge: true,
            },
        }
        return {
            statusList: STATES_DATA,
        }
    },
    data() {
        return {
            showInfoCard: false,
            showErrorsCard: false,
            selected: undefined,
            menuOpen: false,
            menuActivator: null,
            moreInfo: {},
            moreInfoErrorMessage: '',
            isLoadingMoreInfo: false,
            isUpdateModalOpen: false,
            updateMenuOpen: false,
            updateMenuActivator: null,

        }
    },
    computed: {
        showProgressBar() {
            const { uploaded, state } = this.row
            return uploaded && (state?.identifier === STATES_KEYS.SUBMITTED || state?.identifier === STATES_KEYS.RUNNING)
        },
        statusKey() {
            const { uploaded, state } = this.row || {}
            if (!uploaded) return STATES_KEYS.INCOMPLETE
            if (!state?.identifier) return STATES_KEYS.INCOMPLETE
            return state.identifier
        },
        currentStatus() {
            return this.statusList[this.statusKey] || this.statusList.default
        },
        showStatusBadge() {
            return !this.showProgressBar
        },
        canOpenVisualization() {
            const { uploaded, state } = this.row
            return uploaded && state?.identifier === STATES_KEYS.FINISHED
        },
    },
    methods: {
        openMenu(row, event) {
            this.selected = row
            this.menuActivator = event?.currentTarget || event?.target || null
            if (!this.menuActivator) return
            this.menuOpen = true
        },
        cleanSelected() {
            this.menuOpen = false
            this.selected = undefined
            this.menuActivator = null
        },
        handleClickOption(option) {
            if (option.event) {
                this.handleEmmit(option.event, this.selected)
            }
            this.cleanSelected()
        },
        handleEmmit(event) {
            this.$emit('handleEmmit', event)
        },
        handleGoToVisualization() {
            const { uploaded, state } = this.row
            if (uploaded && state?.identifier === STATES_KEYS.FINISHED) this.handleEmmit('onVisualization')
        },
        handleOpenUpdateModal() {
            this.cleanUpdateMenu()
            this.$emit('handleEmmit', 'onUpload')
        },
        handleOpenReplaceModal() {
            this.cleanUpdateMenu()
            this.$emit('handleEmmit', 'onReplace')
        },
        handleInfoModal() {
            this.$emit('handleEmmit', 'onSettings')
        },
        openUpdateMenu(event) {
            this.updateMenuActivator = event?.currentTarget || event?.target || null
            if (!this.updateMenuActivator) return
            this.updateMenuOpen = true
        },

        cleanUpdateMenu() {
            this.updateMenuOpen = false
            this.updateMenuActivator = null
        },
    },
}
</script>
<style scoped lang="scss">
.DatasetCard {
        overflow: hidden;
        height: 100%;
}

.DatasetCardTitle {
        max-width: 100%;
}

.DatasetCardRow {
        display: grid;
        grid-template-columns: 1fr;
        grid-row-gap: 2px;
}

:deep(.DatasetProgress .q-linear-progress__track) {
        opacity: 0.25;
}
</style>
